import { useCallback } from 'react';
import { useUploadWizardContext } from '../context/UploadWizardContext';
import {
  UploadFileGroup,
  UploadVersionType,
} from '../types';
import { getDisplayBaseName } from '@utils/fileGrouping';
import { detectVersionType } from '@utils/fileGrouping';

function selectMainFileName(
  fileNames: string[],
  versionTypes: Record<string, UploadVersionType>
): string {
  const DERIVED = new Set(['tagged', 'clean', 'stems']);
  const nonDerived = fileNames.filter((n) => !DERIVED.has(versionTypes[n] ?? ''));
  const candidates = nonDerived.length > 0 ? nonDerived : fileNames;

  const versionPriority: Record<string, number> = {
    master: 5,
    wav: 4,
    mp3: 2,
    stems: 1,
    tagged: 0,
    clean: 0,
  };

  return candidates.reduce((best, current) => {
    const bestScore = versionPriority[versionTypes[best] ?? ''] ?? 3;
    const currentScore = versionPriority[versionTypes[current] ?? ''] ?? 3;
    return currentScore > bestScore ? current : best;
  });
}

export function useUploadGroups() {
  const { data, setData } = useUploadWizardContext();

  const updateGroups = useCallback(
    (updater: (groups: UploadFileGroup[]) => UploadFileGroup[]) => {
      setData((prev) => ({ groups: updater(prev.groups) }));
    },
    [setData]
  );

  const setMainFile = useCallback(
    (groupId: string, fileName: string) => {
      updateGroups((groups) =>
        groups.map((g) =>
          g.id === groupId ? { ...g, mainFileName: fileName, baseName: getDisplayBaseName(fileName) } : g
        )
      );
    },
    [updateGroups]
  );

  const setVersionType = useCallback(
    (groupId: string, fileName: string, versionType: UploadVersionType) => {
      updateGroups((groups) =>
        groups.map((g) => {
          if (g.id !== groupId) return g;
          const versionTypes = { ...g.versionTypes, [fileName]: versionType };
          return { ...g, versionTypes };
        })
      );
    },
    [updateGroups]
  );

  const splitFile = useCallback(
    (fileName: string) => {
      updateGroups((groups) => {
        const sourceGroup = groups.find((g) => g.fileNames.includes(fileName));
        if (!sourceGroup) return groups;

        const remaining = sourceGroup.fileNames.filter((n) => n !== fileName);
        const versionType = sourceGroup.versionTypes[fileName] ?? detectVersionType(fileName) ?? null;

        const newSingleton: UploadFileGroup = {
          id: crypto.randomUUID(),
          baseName: getDisplayBaseName(fileName),
          fileNames: [fileName],
          mainFileName: fileName,
          versionTypes: { [fileName]: versionType },
          bpm: '',
          musicalKey: '',
          tags: [],
        };

        if (remaining.length === 0) {
          return [...groups.filter((g) => g.id !== sourceGroup.id), newSingleton];
        }

        const versionTypes: Record<string, UploadVersionType> = {};
        remaining.forEach((n) => {
          versionTypes[n] = sourceGroup.versionTypes[n] ?? detectVersionType(n) ?? null;
        });
        const mainFileName = selectMainFileName(remaining, versionTypes);

        const updatedSource: UploadFileGroup = {
          ...sourceGroup,
          fileNames: remaining,
          mainFileName,
          versionTypes,
          baseName: getDisplayBaseName(mainFileName),
        };

        return groups
          .map((g) => (g.id === sourceGroup.id ? updatedSource : g))
          .concat(newSingleton);
      });
    },
    [updateGroups]
  );

  const moveFile = useCallback(
    (fileName: string, targetGroupId: string) => {
      updateGroups((groups) => {
        const sourceGroup = groups.find((g) => g.fileNames.includes(fileName));
        const targetGroup = groups.find((g) => g.id === targetGroupId);
        if (!sourceGroup || !targetGroup || sourceGroup.id === targetGroup.id) return groups;

        const versionType = sourceGroup.versionTypes[fileName] ?? detectVersionType(fileName) ?? null;

        const sourceRemaining = sourceGroup.fileNames.filter((n) => n !== fileName);
        const targetFileNames = [...targetGroup.fileNames, fileName];

        const targetVersionTypes: Record<string, UploadVersionType> = {
          ...targetGroup.versionTypes,
          [fileName]: versionType,
        };
        const targetMain = selectMainFileName(targetFileNames, targetVersionTypes);

        const updatedTarget: UploadFileGroup = {
          ...targetGroup,
          fileNames: targetFileNames,
          mainFileName: targetMain,
          versionTypes: targetVersionTypes,
          baseName: getDisplayBaseName(targetMain),
        };

        let result = groups.map((g) => (g.id === targetGroup.id ? updatedTarget : g));

        if (sourceRemaining.length === 0) {
          result = result.filter((g) => g.id !== sourceGroup.id);
        } else {
          const sourceVersionTypes: Record<string, UploadVersionType> = {};
          sourceRemaining.forEach((n) => {
            sourceVersionTypes[n] = sourceGroup.versionTypes[n] ?? detectVersionType(n) ?? null;
          });
          const sourceMain = selectMainFileName(sourceRemaining, sourceVersionTypes);
          const updatedSource: UploadFileGroup = {
            ...sourceGroup,
            fileNames: sourceRemaining,
            mainFileName: sourceMain,
            versionTypes: sourceVersionTypes,
            baseName: getDisplayBaseName(sourceMain),
          };
          result = result.map((g) => (g.id === sourceGroup.id ? updatedSource : g));
        }

        return result;
      });
    },
    [updateGroups]
  );

  const mergeGroups = useCallback(
    (groupIdA: string, groupIdB: string) => {
      if (groupIdA === groupIdB) return;
      updateGroups((groups) => {
        const groupA = groups.find((g) => g.id === groupIdA);
        const groupB = groups.find((g) => g.id === groupIdB);
        if (!groupA || !groupB) return groups;

        const fileNames = [...new Set([...groupA.fileNames, ...groupB.fileNames])];
        const versionTypes: Record<string, UploadVersionType> = {
          ...groupA.versionTypes,
          ...groupB.versionTypes,
        };
        const mainFileName = selectMainFileName(fileNames, versionTypes);

        const merged: UploadFileGroup = {
          id: groupA.id,
          baseName: getDisplayBaseName(mainFileName),
          fileNames,
          mainFileName,
          versionTypes,
          bpm: groupA.bpm || groupB.bpm,
          musicalKey: groupA.musicalKey || groupB.musicalKey,
          tags: groupA.tags.length > 0 ? groupA.tags : groupB.tags,
        };

        return groups.filter((g) => g.id !== groupIdB).map((g) => (g.id === groupIdA ? merged : g));
      });
    },
    [updateGroups]
  );

  const updateGroup = useCallback(
    (groupId: string, updates: Partial<UploadFileGroup>) => {
      updateGroups((groups) =>
        groups.map((g) => (g.id === groupId ? { ...g, ...updates } : g))
      );
    },
    [updateGroups]
  );

  const getFileSize = useCallback(
    (fileName: string) => data.files.find((f) => f.name === fileName)?.size ?? 0,
    [data.files]
  );

  return {
    groups: data.groups,
    files: data.files,
    setMainFile,
    setVersionType,
    splitFile,
    moveFile,
    mergeGroups,
    updateGroup,
    getFileSize,
  };
}
